import { useState, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';
import { CodingAPI, type Matrix } from '../api/coding';
import { Card, PageHeader, ErrorBanner, InfoBanner, OkBanner } from '../components/Layout';
import ProjectFormPicker from '../components/ProjectFormPicker';
import SurveyPlan from '../components/coding/SurveyPlan';
import CodingSeries from '../components/coding/CodingSeries';
import CodingWork from '../components/coding/CodingWork';
import MatrixEditor from '../components/coding/MatrixEditor';
import GenerateMatrixModal from '../components/coding/GenerateMatrixModal';

function download(value: unknown, name: string) {
  const href = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  a.click();
  URL.revokeObjectURL(href);
}

export default function CodingPage() {
  const [projectId, setProject] = useState<number | null>(null);
  const [formId, setForm] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [editing, setEditing] = useState<{ bookId?: number; spec?: Matrix; name?: string } | null>(null);
  const [isLibraryIaModalOpen, setIsLibraryIaModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'plan' | 'matrices' | 'runs' | 'review' | 'releases' | 'series'>('plan');

  // Estados locales optimistas para respuesta inmediata de la interfaz
  const [enabled, setEnabled] = useState<boolean>(false);
  const [automatic, setAutomatic] = useState<boolean>(false);
  const [intervalMinutes, setIntervalMinutes] = useState<number>(60);
  const [autoUseAI, setAutoUseAI] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);

  const { me } = useAuthStore();
  const qc = useQueryClient();

  const surveys = useQuery({
    queryKey: ['coding-surveys', projectId, formId],
    queryFn: () => CodingAPI.surveys(projectId!, formId!),
    enabled: !!projectId && !!formId,
  });
  const survey = surveys.data?.[0];

  const canMatrices = me?.role === 'admin' || survey?.capabilities.includes('matrices');
  const books = useQuery({
    queryKey: ['coding-books', projectId],
    queryFn: () => CodingAPI.books(projectId!),
    enabled: !!projectId && !!canMatrices,
  });

  // Sincronizar el estado local con los datos remotos de la encuesta
  useEffect(() => {
    if (survey) {
      setEnabled(Boolean(survey.enabled));
      setAutomatic(Boolean(survey.automatic));
      setIntervalMinutes(survey.interval_minutes || 60);
      setAutoUseAI(Boolean(survey.options?.automatic_use_ai));
    }
  }, [
    survey?.id,
    survey?.enabled,
    survey?.automatic,
    survey?.interval_minutes,
    survey?.options?.automatic_use_ai,
  ]);

  const action = useMutation({
    mutationFn: async (job: () => Promise<unknown>) => job(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['coding-surveys'] });
      void qc.invalidateQueries({ queryKey: ['coding-books'] });
      setNotice('Cambios guardados con éxito.');
      setErrorMsg('');
    },
    onError: (err) => {
      setErrorMsg((err as Error).message || 'Error al guardar los cambios.');
    },
  });

  const run = (job: () => Promise<unknown>) => {
    setNotice('');
    setErrorMsg('');
    action.mutate(job);
  };

  // Manejadores directos con actualización inmediata y rollback en caso de fallo
  const handleToggleEnabled = async (checked: boolean) => {
    if (!survey) return;
    const prev = enabled;
    setEnabled(checked);
    setNotice('');
    setErrorMsg('');
    try {
      await CodingAPI.configure(survey.id, { enabled: checked });
      setNotice(checked ? 'Codificación habilitada en esta encuesta.' : 'Codificación deshabilitada en esta encuesta.');
      await qc.invalidateQueries({ queryKey: ['coding-surveys', projectId, formId] });
    } catch (e) {
      setEnabled(prev);
      setErrorMsg((e as Error).message || 'Error al cambiar la habilitación de codificación.');
    }
  };

  const handleToggleAutomatic = async (checked: boolean) => {
    if (!survey) return;
    const prev = automatic;
    setAutomatic(checked);
    setNotice('');
    setErrorMsg('');
    try {
      await CodingAPI.configure(survey.id, { automatic: checked });
      setNotice(checked ? 'Actualización y codificación automática activada para casos nuevos.' : 'Actualización automática desactivada.');
      await qc.invalidateQueries({ queryKey: ['coding-surveys', projectId, formId] });
    } catch (e) {
      setAutomatic(prev);
      setErrorMsg((e as Error).message || 'Error al cambiar la actualización automática.');
    }
  };

  const handleIntervalBlur = async (val: number) => {
    if (!survey || isNaN(val) || val < 5) return;
    try {
      await CodingAPI.configure(survey.id, { interval_minutes: val });
      setNotice(`Intervalo actualizado a ${val} minutos.`);
      await qc.invalidateQueries({ queryKey: ['coding-surveys', projectId, formId] });
    } catch (e) {
      setErrorMsg((e as Error).message || 'Error al guardar el intervalo de actualización.');
    }
  };

  const handleToggleAutoAI = async (checked: boolean) => {
    if (!survey) return;
    const prev = autoUseAI;
    setAutoUseAI(checked);
    setNotice('');
    setErrorMsg('');
    try {
      await CodingAPI.configure(survey.id, { options: { automatic_use_ai: checked } });
      setNotice(checked ? 'Uso automático de IA activado para respuestas sin regla.' : 'Uso automático de IA desactivado.');
      await qc.invalidateQueries({ queryKey: ['coding-surveys', projectId, formId] });
    } catch (e) {
      setAutoUseAI(prev);
      setErrorMsg((e as Error).message || 'Error al actualizar opciones de IA.');
    }
  };

  const handleSyncODK = async () => {
    if (!survey) return;
    setSyncing(true);
    setNotice('');
    setErrorMsg('');
    try {
      const summary = await CodingAPI.sync(survey.id);
      await qc.invalidateQueries({ queryKey: ['coding-surveys', projectId, formId] });
      setNotice(`Sincronización ODK exitosa: ${summary.new} entrevistas nuevas, ${summary.changed} modificadas, ${summary.unchanged} sin cambios.`);
    } catch (e) {
      setErrorMsg((e as Error).message || 'Falló la sincronización de respuestas con ODK Central.');
    } finally {
      setSyncing(false);
    }
  };

  const publishedVersions = books.data?.flatMap((b) =>
    b.versions.filter((v) => v.status === 'published').map((v) => ({ book: b, version: v }))
  ) ?? [];

  return (
    <div className="p-3 sm:p-6 space-y-4 min-w-0">
      <PageHeader
        title="Codificación"
        subtitle="Convierta respuestas abiertas en variables analizables, conserve cada decisión y reutilice sus matrices entre encuestas."
      />

      <Card>
        <ProjectFormPicker
          projectId={projectId}
          formId={formId}
          onProject={(id) => {
            setProject(id);
            setForm(null);
            setEditing(null);
          }}
          onForm={(id) => {
            setForm(id);
            setEditing(null);
          }}
        />
      </Card>

      {[surveys.error, books.error, action.error].filter(Boolean).map((e, i) => (
        <ErrorBanner key={i} message={(e as Error).message} />
      ))}
      {errorMsg && <ErrorBanner message={errorMsg} />}
      {notice && <OkBanner message={notice} />}
      {action.isPending && <InfoBanner message="Guardando cambios en el servidor…" />}
      {!projectId && <InfoBanner message="Seleccione un proyecto y una encuesta para empezar." />}

      {formId && !survey && !surveys.isLoading && (
        <Card>
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-tinta">Preparar encuesta para codificación</h2>
            <p className="text-sm text-tinta-2">
              Esta encuesta todavía no tiene una base de codificación inicializada. Al prepararla, se crea la estructura persistente para alojar casos, matrices y revisiones.
            </p>
            {me?.role === 'admin' ? (
              <button
                className="btn"
                disabled={action.isPending}
                onClick={() => run(() => CodingAPI.createSurvey(projectId!, formId))}
              >
                {action.isPending ? 'Preparando…' : 'Preparar encuesta ahora'}
              </button>
            ) : (
              <p className="text-sm text-tinta-3">Un administrador puede prepararla y concederle permisos.</p>
            )}
          </div>
        </Card>
      )}

      {survey && (
        <Card>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-grid pb-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-xl font-bold text-tinta">{survey.cases} entrevistas</span>
                <span className="text-sm text-tinta-3">
                  {survey.synced_at
                    ? `Sincronizada: ${new Date(survey.synced_at).toLocaleString()}`
                    : 'Aún sin sincronizar'}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${enabled ? 'bg-estado-exito/15 text-estado-exito' : 'bg-plano text-tinta-3 border border-grid'}`}>
                  {enabled ? '● Codificación activa' : '○ Codificación pausada'}
                </span>
              </div>
              {survey.capabilities.includes('code') && (
                <button
                  className="btn-sec text-sm"
                  disabled={syncing || action.isPending}
                  onClick={handleSyncODK}
                  title="Consulta ODK Central para traer nuevas entrevistas o respuestas modificadas"
                >
                  {syncing ? 'Sincronizando con ODK…' : '🔄 Sincronizar respuestas de ODK'}
                </button>
              )}
            </div>

            {survey.sync_error && <ErrorBanner message={`Error de sincronización previo: ${survey.sync_error}`} />}

            {/* Pasos y flujo recomendado interactivo */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-xs bg-plano p-2.5 rounded-lg border border-grid">
              <button
                type="button"
                className={`p-2 rounded text-left transition-colors min-h-[44px] ${enabled ? 'bg-superficie font-semibold text-tinta border border-grid' : 'text-tinta-3'}`}
                onClick={() => handleToggleEnabled(!enabled)}
              >
                1. Habilitar encuesta {enabled ? '✓' : ''}
              </button>
              <button
                type="button"
                className={`p-2 rounded text-left transition-colors min-h-[44px] ${publishedVersions.length > 0 ? 'bg-superficie font-semibold text-tinta border border-grid' : 'text-tinta-3 hover:bg-superficie'}`}
                onClick={() => setActiveTab('matrices')}
              >
                2. Publicar matriz {publishedVersions.length > 0 ? `(${publishedVersions.length}) ✓` : ''}
              </button>
              <button
                type="button"
                className={`p-2 rounded text-left transition-colors min-h-[44px] ${survey.bindings.length > 0 ? 'bg-superficie font-semibold text-tinta border border-grid' : 'text-tinta-3 hover:bg-superficie'}`}
                onClick={() => setActiveTab('plan')}
              >
                3. Vincular variable {survey.bindings.length > 0 ? `(${survey.bindings.length}) ✓` : ''}
              </button>
              <button
                type="button"
                className="p-2 rounded text-left transition-colors text-tinta-3 hover:bg-superficie min-h-[44px]"
                onClick={() => setActiveTab(survey.bindings.length > 0 ? 'runs' : 'plan')}
              >
                4. Ejecutar y revisar
              </button>
            </div>

            {me?.role === 'admin' && (
              <div className="space-y-3 pt-1">
                <label className="flex items-center gap-3 cursor-pointer text-sm font-medium text-tinta select-none">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded text-marca focus:ring-marca"
                    checked={enabled}
                    onChange={(e) => handleToggleEnabled(e.target.checked)}
                  />
                  <span>Habilitar codificación en esta encuesta</span>
                </label>

                <div className="space-y-2 pl-7 border-l-2 border-grid">
                  <label className="flex items-center gap-3 cursor-pointer text-sm text-tinta select-none">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded text-marca focus:ring-marca"
                      checked={automatic}
                      onChange={(e) => handleToggleAutomatic(e.target.checked)}
                    />
                    <span>Actualizar y codificar automáticamente los casos nuevos</span>
                  </label>

                  {automatic && (
                    <div className="mt-2 space-y-2 p-3 bg-plano rounded border border-grid text-sm">
                      <label className="block text-xs font-medium text-tinta-2">
                        Intervalo entre actualizaciones (minutos):
                        <input
                          className="input mt-1 w-32 block"
                          type="number"
                          min="5"
                          max="10080"
                          value={intervalMinutes}
                          onChange={(e) => setIntervalMinutes(Number(e.target.value))}
                          onBlur={(e) => handleIntervalBlur(Number(e.target.value))}
                        />
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-xs text-tinta-2">
                        <input
                          type="checkbox"
                          checked={autoUseAI}
                          onChange={(e) => handleToggleAutoAI(e.target.checked)}
                        />
                        <span>Usar IA automáticamente para respuestas sin regla inequívoca (consume presupuesto configurado)</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            <p className="text-xs text-tinta-3">
              La sincronización conserva los valores originales y detecta casos nuevos y respuestas modificadas. Las matrices y decisiones se guardan independientemente de la caché de ODK.
            </p>
          </div>
        </Card>
      )}

      {/* Barra de pestañas del flujo de codificación */}
      {survey && (
        <div className="flex items-center gap-1 border-b border-grid pb-1 overflow-x-auto min-w-0 no-scrollbar pt-1">
          {[
            {
              id: 'plan',
              label: '1. Plan y variables',
              icon: '📋',
              badge: survey.dictionary ? `${survey.dictionary.length}` : '',
            },
            {
              id: 'matrices',
              label: '2. Matrices de códigos',
              icon: '🏷️',
              badge: publishedVersions.length ? `${publishedVersions.length} pub.` : books.data?.length ? `${books.data.length}` : '0',
            },
            {
              id: 'runs',
              label: '3. Ejecutar codificación',
              icon: '⚡',
            },
            {
              id: 'review',
              label: '4. Revisión y auditoría',
              icon: '🔍',
              badge: survey.bindings?.length ? `${survey.bindings.length} vin.` : '',
            },
            {
              id: 'releases',
              label: '5. Bases publicadas',
              icon: '📦',
            },
            {
              id: 'series',
              label: '6. Estudios longitudinales',
              icon: '📈',
            },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`px-3 py-2 text-xs font-semibold rounded-md whitespace-nowrap transition-colors flex items-center gap-1.5 min-h-[44px] ${
                activeTab === tab.id
                  ? 'bg-marca text-blanco shadow-sm'
                  : 'bg-plano text-tinta-2 hover:bg-superficie hover:text-tinta'
              }`}
              onClick={() => {
                setActiveTab(tab.id as any);
                setNotice('');
                setErrorMsg('');
              }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              {tab.badge && (
                <span
                  className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-superficie border border-grid text-tinta-3'
                  }`}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Pestaña 1: Plan y variables */}
      {activeTab === 'plan' && survey && (
        <SurveyPlan
          key={survey.id}
          survey={survey}
          books={books.data ?? []}
          canCode={survey.capabilities.includes('code')}
          onOpenMatrixEditor={() => {
            setEditing({});
            setActiveTab('matrices');
          }}
          onOpenMatrixEditorWithSpec={(name, spec) => {
            setEditing({ name, spec });
            setActiveTab('matrices');
          }}
          onGoToMatrices={() => setActiveTab('matrices')}
        />
      )}

      {/* Pestaña 2: Biblioteca y editor de matrices */}
      {activeTab === 'matrices' && (
        <div className="space-y-4">
          {editing && (
            <Card>
              <div className="flex items-center justify-between gap-3 mb-3 border-b border-grid pb-2">
                <h2 className="font-semibold text-lg text-tinta">
                  {editing.bookId ? 'Nueva versión basada en la matriz anterior' : 'Nueva matriz de codificación'}
                </h2>
                <button className="btn-sec text-xs min-h-[44px]" onClick={() => setEditing(null)}>
                  ✕ Cerrar editor
                </button>
              </div>
              <MatrixEditor
                key={`${editing.bookId}-${editing.name}-${JSON.stringify(editing.spec)}`}
                initial={editing.spec}
                initialName={editing.name}
                projectId={projectId ?? 1}
                busy={action.isPending}
                onSave={(name, spec) =>
                  run(async () => {
                    const result = editing.bookId
                      ? await CodingAPI.newVersion(editing.bookId, spec)
                      : await CodingAPI.createBook(projectId!, name, spec);
                    setEditing(null);
                    return result;
                  })
                }
              />
            </Card>
          )}

          {projectId && canMatrices && (
            <Card>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <div>
                  <h2 className="text-lg font-semibold text-tinta">Biblioteca de matrices de codificación</h2>
                  <p className="text-xs text-tinta-3">
                    Cree libros de códigos reutilizables. Cada matriz debe estar en estado «Publicada» para poder vincularse a preguntas de la encuesta.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="btn text-xs min-h-[44px]"
                    onClick={() => setIsLibraryIaModalOpen(true)}
                  >
                    🪄 Generar matriz con IA
                  </button>
                  <button className="btn-sec text-xs min-h-[44px]" onClick={() => setEditing({})}>
                    + Crear o importar matriz
                  </button>
                </div>
              </div>

              {projectId && (
                <GenerateMatrixModal
                  isOpen={isLibraryIaModalOpen}
                  onClose={() => setIsLibraryIaModalOpen(false)}
                  projectId={projectId}
                  onMatrixGenerated={(name, spec) => {
                    setEditing({ name, spec });
                  }}
                />
              )}

              {!books.data?.length && !books.isLoading && (
                <div className="p-4 bg-plano border border-grid rounded text-sm text-tinta-2 space-y-2">
                  <p className="font-medium text-tinta">No hay matrices en este proyecto todavía.</p>
                  <p>Haga clic en «Crear o importar matriz» para definir sus categorías o cargar un archivo JSON.</p>
                </div>
              )}

              {books.data?.map((book) => (
                <div key={book.id} className="border-t border-grid py-3 space-y-2">
                  <h3 className="font-semibold text-tinta">{book.name}</h3>
                  {book.versions.map((v) => (
                    <div key={v.id} className="flex flex-wrap gap-2 items-center text-sm p-2 rounded bg-plano">
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold ${v.status === 'published' ? 'bg-estado-exito/15 text-estado-exito' : 'bg-estado-aviso/15 text-estado-aviso'}`}>
                        Versión {v.number} · {v.status === 'published' ? 'Publicada' : 'Borrador'}
                      </span>
                      <span className="text-tinta-2">({v.specification.codes.length} categorías)</span>

                      <div className="flex-1" />

                      <button className="btn-sec text-xs min-h-[36px]" onClick={() => setEditing({ bookId: book.id, spec: v.specification })}>
                        Editar / Nueva versión
                      </button>
                      <button
                        className="btn-sec text-xs min-h-[36px]"
                        onClick={() => run(async () => download(await CodingAPI.exportMatrix(v.id), `matriz-${book.id}-v${v.number}.json`))}
                      >
                        Exportar JSON
                      </button>
                      {v.status === 'draft' && (
                        <button
                          className="btn text-xs min-h-[36px]"
                          disabled={action.isPending}
                          onClick={() => run(() => CodingAPI.publish(v.id))}
                          title="Fija esta versión y permite vincularla a preguntas de la encuesta"
                        >
                          Publicar matriz
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* Pestaña 3: Ejecutar codificación */}
      {activeTab === 'runs' && survey && (
        <CodingWork
          key={survey.id}
          survey={survey}
          books={books.data ?? []}
          section="runs"
          onNavigate={(t) => setActiveTab(t as any)}
        />
      )}

      {/* Pestaña 4: Revisión y auditoría de decisiones */}
      {activeTab === 'review' && survey && (
        <CodingWork
          key={survey.id}
          survey={survey}
          books={books.data ?? []}
          section="review"
          onNavigate={(t) => setActiveTab(t as any)}
        />
      )}

      {/* Pestaña 5: Bases publicadas y frecuencias */}
      {activeTab === 'releases' && survey && (
        <CodingWork
          key={survey.id}
          survey={survey}
          books={books.data ?? []}
          section="releases"
          onNavigate={(t) => setActiveTab(t as any)}
        />
      )}

      {/* Pestaña 6: Comparación longitudinal */}
      {activeTab === 'series' && projectId && (
        <CodingSeries
          key={projectId}
          projectId={projectId}
          canCreate={survey?.capabilities.includes('code') ?? false}
        />
      )}
    </div>
  );
}
